import { DEFAULT_ICON } from "@circle-react-uikit/Form/FormEmojiPickerV3/constants";
import { useSpaceGroupsContext } from "@circle-react/contexts/spaceGroupsContext";
import type { CourseSetting, SpaceType } from "@circle-react/types/Space";
import { useMemo } from "react";

import type { SpacePrefillData } from "@/react/components/SettingsApp/Spaces/helpers/spacePrefillHelpers";
import { convertPrefillDataToFormDefaults } from "@/react/components/SettingsApp/Spaces/helpers/spacePrefillHelpers";
import { DEFAULT_COURSE_TYPE } from "@/react/components/Spaces/CourseSpace/CourseTypePicker/constants";

import { DEFAULT_NOTIFICATION_TOGGLES } from "../constants";
import type {
  ChatNotificationLevel,
  NotificationToggleKey,
  SpaceFormValues,
} from "../types";
import { isBasicPostType, isChatPostType } from "../utils/spaceTypePredicates";

interface UseSpaceFormDefaultsArgs {
  canCreateAllTypesOfSpaces: boolean;
  courseSetting?: CourseSetting;
  currentSpaceGroupId?: number;
  post_type: SpaceType;
  prefillData?: SpacePrefillData;
}

export const useSpaceFormDefaults = ({
  canCreateAllTypesOfSpaces,
  courseSetting = { course_type: DEFAULT_COURSE_TYPE },
  currentSpaceGroupId,
  post_type,
  prefillData,
}: UseSpaceFormDefaultsArgs): SpaceFormValues => {
  const {
    helpers: { findBy },
  } = useSpaceGroupsContext();
  const currentSpaceGroup = findBy({ id: currentSpaceGroupId });

  return useMemo<SpaceFormValues>(() => {
    const notification_type: NotificationToggleKey[] | ChatNotificationLevel =
      isChatPostType(post_type) ? "never" : DEFAULT_NOTIFICATION_TOGGLES;

    const base: SpaceFormValues = {
      name: "",
      space_group_id: currentSpaceGroupId,
      post_type: canCreateAllTypesOfSpaces ? post_type : "basic",
      visibility: "open",
      course_setting: courseSetting,
      emoji: "#",
      custom_emoji: DEFAULT_ICON,
      notification_type,
      is_post_disabled: true,
      event_auto_rsvp_enabled: false,
      add_members_from_space_group:
        currentSpaceGroup?.automatically_add_members_to_new_spaces,
      default_comment_sort: "oldest",
      default_member_sort: "latest",
      default_sort: "latest",
      hide_right_sidebar: isBasicPostType(post_type),
    };

    const prefillDefaults = prefillData
      ? convertPrefillDataToFormDefaults(prefillData, post_type)
      : {};

    return { ...base, ...prefillDefaults } as SpaceFormValues;
  }, [
    canCreateAllTypesOfSpaces,
    courseSetting,
    currentSpaceGroup?.automatically_add_members_to_new_spaces,
    currentSpaceGroupId,
    post_type,
    prefillData,
  ]);
};
